<script lang="ts">
  /**
   * Pre-room panels: exchanging an invite, and choosing a display name.
   * Rendered only after the URL fragment has already been consumed and
   * stripped, so nothing here can leak a secret into the address bar.
   */
  import { parsePastedInvite } from '../session/invite';
  import { checkUsername, USERNAME_MAX_LENGTH } from '../session/username';

  const {
    stage,
    message,
    busy,
    suggestedName,
    onInvite,
    onUsername,
  }: {
    stage: 'invite' | 'username';
    message: string | null;
    busy: boolean;
    suggestedName: string;
    onInvite: (secret: string) => void;
    onUsername: (username: string) => void;
  } = $props();

  let inviteInput = $state('');
  /** `null` until the user types, so a changed suggestion still flows through. */
  let nameEdit = $state<string | null>(null);
  const nameValue = $derived(nameEdit ?? suggestedName);
  let error = $state<string | null>(null);
  let firstField = $state<HTMLInputElement | null>(null);

  $effect(() => {
    // Focus follows the stage so a keyboard-only join needs no pointer. The
    // suggested name is selected, so typing replaces it instead of appending
    // to a value the user did not choose.
    firstField?.focus();
    firstField?.select();
  });

  function submitInvite(event: SubmitEvent): void {
    event.preventDefault();
    const secret = parsePastedInvite(inviteInput);
    if (secret === null) {
      error = 'That does not look like an invite link. Paste the whole link you were given.';
      return;
    }
    error = null;
    onInvite(secret);
  }

  function submitUsername(event: SubmitEvent): void {
    event.preventDefault();
    const checked = checkUsername(nameValue);
    if (!checked.ok) {
      error = checked.message;
      return;
    }
    error = null;
    onUsername(checked.username);
  }
</script>

<main>
  <h1>WatchAlong</h1>

  {#if stage === 'invite'}
    <section aria-labelledby="invite-heading">
      <h2 id="invite-heading">Join the room</h2>
      <p>
        This room is invite-only. Open the link you were given, or paste it below. The secret in
        the link is exchanged for a session cookie and removed from the address bar immediately.
      </p>
      <form onsubmit={submitInvite}>
        <div class="field">
          <label for="invite">Invite link</label>
          <input
            bind:this={firstField}
            id="invite"
            type="text"
            autocomplete="off"
            spellcheck="false"
            placeholder="https://watchalong.example/#s=…"
            bind:value={inviteInput}
            aria-invalid={error !== null}
          />
        </div>
        <button type="submit" class="primary" disabled={busy}>
          {busy ? 'Joining…' : 'Join'}
        </button>
      </form>
    </section>
  {:else}
    <section aria-labelledby="name-heading">
      <h2 id="name-heading">Pick a display name</h2>
      <p>
        Other people in the room see this name. It is remembered in this browser only and is not a
        password — you can change it at any time.
      </p>
      <form onsubmit={submitUsername}>
        <div class="field">
          <label for="join-username">Display name</label>
          <input
            bind:this={firstField}
            id="join-username"
            type="text"
            autocomplete="nickname"
            maxlength={USERNAME_MAX_LENGTH}
            value={nameValue}
            oninput={(event) => (nameEdit = event.currentTarget.value)}
            aria-invalid={error !== null}
          />
          <p class="hint">Up to {USERNAME_MAX_LENGTH} characters. Duplicates are allowed.</p>
        </div>
        <button type="submit" class="primary" disabled={busy}>Enter the room</button>
      </form>
    </section>
  {/if}

  {#if error !== null}
    <p class="alert" role="alert">{error}</p>
  {:else if message !== null}
    <p class="alert" role="alert">{message}</p>
  {/if}
</main>

<style>
  main {
    max-width: 34rem;
    margin: 0 auto;
    padding: 4rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  p {
    margin: 0.5rem 0 0;
    color: var(--wa-text-dim);
  }

  form {
    display: flex;
    gap: 0.75rem;
    align-items: flex-end;
    margin-top: 0.9rem;
  }

  .field {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
  }

  .alert {
    background: var(--wa-danger-surface);
    border: 1px solid #6c2f36;
    border-radius: 0.4rem;
    color: var(--wa-danger);
    padding: 0.6rem 0.75rem;
  }
</style>
